"use client";

import { motion, useReducedMotion, type Variants } from "motion/react";
import { Fragment, useEffect, useRef, useState } from "react";

// Slide subtil — opacité 1 dès le premier frame pour que mix-blend-mode
// reste actif sans interruption. Pas de overflow-hidden : les mots ne
// sont jamais clippés et donc jamais "invisibles" pendant le slide.
const wordVariants: Variants = {
  hidden: { y: 28 },
  show: { y: 0 },
};

export function WordCascade({
  text,
  className,
  delay = 0,
  stagger = 0.06,
  duration = 0.6,
  once = true,
}: {
  text: string;
  className?: string;
  delay?: number;
  stagger?: number;
  duration?: number;
  once?: boolean;
}) {
  const reduce = useReducedMotion();
  const words = text.split(" ");

  if (reduce) {
    return <span className={className}>{text}</span>;
  }

  return (
    <motion.span
      className={className}
      initial="hidden"
      animate="show"
      transition={{ staggerChildren: stagger, delayChildren: delay }}
    >
      {/* Source of truth for screen readers — the per-word spans below are
          aria-hidden so the cascade animation isn't read out one word at a
          time. axe-core flags `aria-label` on a plain <span> as prohibited,
          so we expose the text via a sr-only sibling instead. */}
      <span className="sr-only">{text}</span>
      {words.map((word, i) => (
        // Space lives outside the inline-block so the parent's inline formatting
        // context preserves it. Putting " " *inside* the inline-block lets the
        // browser collapse the trailing whitespace and erases the gap between
        // words (especially with tight negative tracking on large headings).
        <Fragment key={`${word}-${i}`}>
          <motion.span
            aria-hidden
            className="inline-block"
            variants={wordVariants}
            transition={{ duration, ease: [0.22, 1, 0.36, 1] }}
          >
            {word}
          </motion.span>
          {i < words.length - 1 ? " " : ""}
        </Fragment>
      ))}
      {!once && null}
    </motion.span>
  );
}

const SCRAMBLE_GLYPHS = "▓░█▒/+×*=<>".split("");

function pickGlyph(original: string) {
  if (original === " " || original === " ") return original;
  return SCRAMBLE_GLYPHS[Math.floor(Math.random() * SCRAMBLE_GLYPHS.length)];
}

export function Scramble({
  text,
  className,
  duration = 1500,
  delay = 0,
  trigger = "view",
  hold = 0,
}: {
  text: string;
  className?: string;
  duration?: number;
  delay?: number;
  trigger?: "mount" | "view";
  hold?: number;
}) {
  const reduce = useReducedMotion();
  const ref = useRef<HTMLSpanElement | null>(null);
  // Always render the real text on first paint so SSR markup matches client
  // hydration. The scramble animation begins after mount.
  const [display, setDisplay] = useState<string>(text);
  const startedRef = useRef(false);

  useEffect(() => {
    if (reduce) {
      setDisplay(text);
      return;
    }

    // Seed the scrambled state client-side once mounted (post-hydration).
    setDisplay(
      text
        .split("")
        .map((c) => pickGlyph(c))
        .join("")
    );

    let observer: IntersectionObserver | null = null;
    let startTimeout: ReturnType<typeof setTimeout> | null = null;
    let rafId: number | null = null;

    const start = () => {
      if (startedRef.current) return;
      startedRef.current = true;
      startTimeout = setTimeout(() => {
        const begin = performance.now();
        const total = duration;
        const tick = (now: number) => {
          const elapsed = now - begin;
          const t = Math.min(1, Math.max(0, (elapsed - hold) / total));
          const reveal = Math.floor(t * text.length);
          let out = "";
          for (let i = 0; i < text.length; i++) {
            const ch = text[i];
            if (i < reveal || ch === " " || ch === " ") out += ch;
            else out += pickGlyph(ch);
          }
          setDisplay(out);
          if (t < 1) rafId = requestAnimationFrame(tick);
          else setDisplay(text);
        };
        rafId = requestAnimationFrame(tick);
      }, delay);
    };

    if (trigger === "mount") {
      start();
    } else if (ref.current) {
      observer = new IntersectionObserver(
        (entries) => {
          if (entries[0]?.isIntersecting) {
            start();
            observer?.disconnect();
          }
        },
        { threshold: 0.4 }
      );
      observer.observe(ref.current);
    }

    return () => {
      observer?.disconnect();
      if (startTimeout) clearTimeout(startTimeout);
      if (rafId) cancelAnimationFrame(rafId);
    };
  }, [text, duration, delay, trigger, hold, reduce]);

  return (
    <span ref={ref} className={className}>
      <span className="sr-only">{text}</span>
      <span aria-hidden>{display}</span>
    </span>
  );
}
